import { TIBBO_DEVICE_ID } from '@appblocks/platforms/PlatformUtils';
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import FeatureUtils from '@appblocks/codegeneration/FeatureUtils';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    SettingType,
    CodeBlock,
    BlockType,
    Define,
    SettingState,
    SettingOption,
    ValidationMessage,
    ValidationMessageType,
    FeatureType,
    Setting,
    AppBlockPort,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import { indirectEvalSync } from '@appblocks/utils';
import BaseNode from '../Nodes/BaseNode';


export default class CustomFeatureBase extends BaseFeature {
    constructor(id: string) {
        super();
        this.id = id;
        this.name = 'Custom Feature Base';
        this.linkedFeature = 'general';
    }

    getTemplateFeature(): BaseFeature | undefined {
        const project = this.project;
        const featureId = this.id;
        const customFeatures = project.getFeature('custom_features');
        if (!customFeatures) {
            return undefined;
        }
        const features = customFeatures.getSettingValue('FEATURES');
        if (!features) {
            return undefined;
        }
        const feature = features.find((f: any) => f.getSettingValue('FEATURE_ID') === featureId);
        if (!feature) {
            return undefined;
        }
        return feature;
    }

    initSettings(project?: any, parentFeature?: any, defaultValue?: string) {
        if (project !== undefined) {
            this.project = project;
        }
        const feature = this.getTemplateFeature();
        if (!feature) {
            return;
        }
        const TYPE = feature.getSettingValue('TYPE');
        const RUNTIME = feature.getSettingValue('RUNTIME');
        const SETTINGS = feature.getSettingValue('SETTINGS');
        this.name = feature.getSettingValue('NAME');
        for (let i = 0; i < SETTINGS.length; i++) {
            const settingId = SETTINGS[i].getSettingValue('SETTING_ID');
            const SETTING_TYPE = SETTINGS[i].getSettingValue('TYPE');
            const DESCRIPTION = SETTINGS[i].getSettingValue('DESCRIPTION');
            let settingType = SETTING_TYPE;
            let refId;
            let dataType = SETTINGS[i].getSettingValue('DATA_TYPE');
            if (SETTING_TYPE === 'value') {
                settingType = SettingType.STRINGVARIABLE;
            }
            if (SETTING_TYPE === 'array') {
                settingType = SettingType.ARRAY;
                refId = SETTINGS[i].getSettingValue('REFID');
                dataType = undefined;
            }
            if (SETTING_TYPE === 'feature') {
                settingType = SettingType.OPTION;
                dataType = undefined;
            }
            if (SETTING_TYPE === 'name') {
                settingType = SettingType.NAME;
                dataType = undefined;
            }
            // eslint-disable-next-line no-undef-init
            let setting: Setting | undefined = undefined;
            for (let j = 0; j < this.settings.length; j++) {
                if (this.settings[j].id === settingId) {
                    setting = this.settings[j];
                    break;
                }
            }
            if (!setting) {
                setting = {
                    id: settingId,
                    name: SETTINGS[i].getSettingValue('NAME'),
                    default: SETTINGS[i].getSettingValue('DEFAULT_VALUE'),
                    dataType,
                    settingType,
                    refId,
                };
                this.settings.push(setting);
            }
            setting.name = SETTINGS[i].getSettingValue('NAME');
            setting.settingType = settingType;
            setting.default = SETTINGS[i].getSettingValue('DEFAULT_VALUE');
            setting.required = SETTINGS[i].getSettingValue('REQUIRED') === '1';
            setting.helpText = DESCRIPTION !== '' ? DESCRIPTION : undefined;
            if (SETTING_TYPE === 'option') {
                setting.options = SETTINGS[i].getSettingValue('OPTIONS').map((option: any) => ({
                    name: option.getSettingValue('LABEL'),
                    value: option.getSettingValue('VALUE'),
                }));
            }
            if (SETTING_TYPE === 'pin') {
                setting.meta = SETTINGS[i].getSettingValue('PIN_REQUIREMENT');
            }
            if (SETTING_TYPE === 'array') {
                setting.default = [];
            }
        }
        if (TYPE === 'service') {
            let found = false;
            for (let i = 0; i < this.settings.length; i++) {
                if (this.settings[i].id === 'ENABLED') {
                    found = true;
                    break;
                }
            }
            if (!found) {
                this.settings.unshift({
                    id: 'ENABLED',
                    name: 'Enabled',
                    settingType: SettingType.OPTION,
                    default: '0',
                    options: [
                        {
                            name: 'Disabled',
                            value: '0',
                        },
                        {
                            name: 'Enabled',
                            value: '1',
                        },
                    ],
                });
            }
        }
        if (TYPE === 'peripheral') {
            this.featureType = FeatureType.PERIPHERAL;
        }
        if (TYPE === 'block') {
            // copy from BaseNode
            const baseNode = new BaseNode();
            baseNode.initSettings(project);
            for (let i = 0; i < baseNode.settings.length; i++) {
                const setting = baseNode.settings[i];
                let found = false;
                for (let j = 0; j < this.settings.length; j++) {
                    if (this.settings[j].id === setting.id) {
                        found = true;
                        break;
                    }
                }
                if (!found) {
                    this.settings.push(setting);
                }
            }
        }
        super.initSettings(project, parentFeature, defaultValue);
    }

    getInputs(): AppBlockPort[] {
        // return this.getSettingValue('INPUTS');
        const feature = this.getTemplateFeature();
        const NAME = feature?.getSettingValue('NAME');
        if (NAME !== undefined && NAME.indexOf('On ') === 0) {
            return [];
        }
        return [
            {
                name: 'Run',
                portId: 'exec',
                params: [],
            },
        ];
    }

    getDependencies(): string[] {
        const feature = this.getTemplateFeature();
        if (!feature) {
            return [];
        }
        const DEPENDENCIES = feature.getSettingValue('DEPENDENCIES');
        if (DEPENDENCIES === '') {
            return [];
        }
        return DEPENDENCIES.split(',');
    }

    getOutputs(): AppBlockPort[] {
        // return this.getSettingValue('OUTPUTS');
        const parameters: any = [];
        const project = this.project;
        const feature = this.getTemplateFeature();
        if (!feature) {
            return [];
        }
        const variables = feature.getSettingValue('VARIABLES');
        for (let i = 0; i < variables.length; i++) {
            const variable = variables[i];
            parameters.push({
                name: variable.getSettingValue('NAME'),
                dataType: variable.getSettingValue('DATA_TYPE'),
            });
        }
        return [
            {
                name: 'Done',
                portId: 'done',
                params: parameters,
            },
        ];
    }

    getPort(portId: string): AppBlockPort | undefined {
        const ports = this.getInputs().concat(this.getOutputs());
        for (let i = 0; i < ports.length; i += 1) {
            if (ports[i].portId === portId) {
                return ports[i];
            }
        }
        return undefined;
    }

    getNestedFeatures(features: BaseFeature[], capability: string): BaseFeature[] {
        const compatibleFeatures: BaseFeature[] = [];
        for (let i = 0; i < features.length; i++) {
            const feature = features[i];
            if (feature.capabilities && feature.capabilities.indexOf(capability) !== -1) {
                compatibleFeatures.push(feature);
            }
            if (feature.id === capability) {
                compatibleFeatures.push(feature);
            }
            for (let j = 0; j < feature.settings.length; j++) {
                if (feature.settings[j].settingType === SettingType.ARRAY) {
                    for (let k = 0; k < feature.settings[j].value.length; k++) {
                        const arrayItem = feature.settings[j].value[k] as BaseFeature;
                        compatibleFeatures.push(...this.getNestedFeatures([arrayItem], capability));
                    }
                }
            }
        }
        return compatibleFeatures;
    }

    getSettingOptions(settingId: string): SettingOption[] | undefined {
        const feature = this.getTemplateFeature();
        if (feature) {
            const SETTINGS = feature.getSettingValue('SETTINGS');
            for (let i = 0; i < SETTINGS.length; i++) {
                if (SETTINGS[i].getSettingValue('SETTING_ID') === settingId) {
                    const SETTING_TYPE = SETTINGS[i].getSettingValue('TYPE');
                    if (SETTING_TYPE === 'feature') {
                        const FEATURE_CAPABILITY = SETTINGS[i].getSettingValue('FEATURE_CAPABILITY');
                        const project = this.project;
                        const options = [];
                        options.push({
                            name: 'Undefined',
                            value: '',
                        });
                        // find all features that have the capability nested
                        const nestedFeatures = this
                            .getNestedFeatures(project.features, FEATURE_CAPABILITY);
                        for (let j = 0; j < nestedFeatures.length; j++) {
                            const nestedFeature = nestedFeatures[j];
                            options.push({
                                name: nestedFeature.getName(),
                                value: nestedFeature.getID(),
                            });
                        }
                        return options;
                    }
                }
            }
        }
        return super.getSettingOptions(settingId);
    }

    getState(settingId: string): SettingState {
        if (!this.getEnabled() && settingId !== 'ENABLED') {
            return SettingState.DISABLED;
        }
        const featureId = this.id;
        if (featureId.indexOf('node_') === 0) {
            switch (settingId) {
                case 'DISPLAY_NAME':
                case 'FEATURE':
                case 'X':
                case 'Y':
                case 'INPUTS':
                case 'OUTPUTS':
                case 'ARGUMENTS':
                    return SettingState.HIDDEN;
                default:
                    return SettingState.ENABLED;
            }
        }
        return super.getState(settingId);
    }

    getFiles(): string[] {
        const files = [];
        const project = this.project;
        const feature = this.getTemplateFeature();
        if (!feature) {
            return [];
        }
        const CODE_BLOCKS = feature.getSettingValue('CODE_BLOCKS');
        for (let i = 0; i < CODE_BLOCKS.length; i++) {
            const block = CODE_BLOCKS[i];
            const TYPE = block.getSettingValue('TYPE');
            if (TYPE === 'file') {
                files.push(block.getSettingValue('NAME'));
            }
        }
        return files;
    }

    parseExpression(expression: string, context: any) {
        let finalCode: string = expression;
        let expressionIndex = finalCode.indexOf('{{');
        const project = this.project;
        let sourceParams: any = {};
        if (this.id.indexOf('node_') === 0) {
            const links = project.getNodeLinks(this, 'exec');
            if (links.length > 0) {
                const source = project.getFeature(links[0].getSettingValue('SOURCE_FEATURE')) as BaseNode;
                sourceParams = source.getPort(links[0].getSettingValue('SOURCE_PORT'))?.params;
            }
        }
        // see if parent feature is custom node
        const parentFeature = project.getParent(this);
        if (parentFeature) {
            const parentFeatureId = parentFeature.id;
            if (parentFeatureId.indexOf('node_') === 0) {
                const links = project.getNodeLinks(parentFeature, 'exec');
                if (links.length > 0) {
                    const source = project.getFeature(links[0].getSettingValue('SOURCE_FEATURE')) as BaseNode;
                    sourceParams = source.getPort(links[0].getSettingValue('SOURCE_PORT'))?.params;
                }
            }
        }
        while (expressionIndex !== -1) {
            const endExpressionIndex = finalCode.indexOf('}}', expressionIndex);
            const expression = finalCode
                .substring(expressionIndex, endExpressionIndex + 2);
            let expressionParsed = finalCode
                .substring(expressionIndex + 2, endExpressionIndex);
            if (expressionParsed.indexOf('$') === 0) {
                // variable expression
                let value = '';
                const featureId = expressionParsed.substring(1);
                const feature = project.getFeature(featureId);
                let dataType: TibboDataType | undefined = TibboDataType.STRING;
                if (feature) {
                    expressionParsed = `{{${expressionParsed}}}`;
                }
                const settingId = expressionParsed.substring(1);
                if (context.feature.settings[settingId] !== undefined) {
                    // setting expression
                    value = this.getSettingValue(settingId);
                    dataType = this.getSetting(settingId)?.dataType;
                    expressionParsed = value;
                } else if (expression.indexOf('{{') !== -1) {
                    expressionParsed = expression;
                }
                expressionParsed = project
                    .parseCodeExpression(expressionParsed, sourceParams, dataType);
                // } else if (expressionParsed.indexOf('feature.settings') === 0
                //     && expressionParsed.indexOf('feature.settings.ID') !== 0) {
                //     const parts = expressionParsed.split('.');
                //     if (parts.length > 2) {
                //         const settingId = parts[2];
                //         const value = this.getSettingValue(settingId);
                //         const dataType = this.getSetting(settingId)?.dataType;
                //         expressionParsed = value;
                //         expressionParsed = project
                //             .parseCodeExpression(expressionParsed, sourceParams, dataType);
                //         if (expressionParsed.indexOf('$') === 0) {
                //             expressionParsed = project
                //                 .parseCodeExpression(`{{${expressionParsed}}}`, sourceParams, dataType);
                //         }
                //     }
            } else {
                // eslint-disable-next-line no-await-in-loop
                expressionParsed = indirectEvalSync(context, expressionParsed);
            }
            finalCode = finalCode.substring(0, expressionIndex)
                + expressionParsed
                + finalCode.substring(endExpressionIndex + 2);
            expressionIndex = finalCode.indexOf('{{');
        }
        return finalCode;
    }

    getCodeBlocks() {
        let blocks: Array<CodeBlock> = [];
        const project = this.project;
        const feature = this.getTemplateFeature();
        if (!feature) {
            return blocks;
        }
        const FEATURE_TYPE = feature.getSettingValue('TYPE');
        if (FEATURE_TYPE === 'service') {
            if (!this.getEnabled()) {
                return blocks;
            }
        }
        const SETTINGS = feature.getSettingValue('SETTINGS');
        const VARIABLES = feature.getSettingValue('VARIABLES');
        const CODE_BLOCKS = feature.getSettingValue('CODE_BLOCKS');
        const NAME = feature.getSettingValue('NAME');
        let triggerCode = '';
        const parentFeature = project.getParent(this);
        let parent;
        if (parentFeature) {
            parent = FeatureUtils.toJson(parentFeature);
        }

        const doneLinks = project.getNodeLinks(this, 'done');
        // javascript expression
        const context: any = {
            feature: FeatureUtils.toJson(this),
            project: project.toJson(),
            block: {
                output: '',
            },
            parent,
        };

        if (NAME.indexOf('On ') === 0) {
            if (doneLinks.length > 0) {
                const link = doneLinks[0];
                context.block.output = `${project.getCodeLocationStart()}${link.getSourceId()}${project.getCodeLocationEnd()}`;
            }
        }

        for (let i = 0; i < this.settings.length; i++) {
            const setting = this.settings[i];
            if (setting.settingType === SettingType.ARRAY) {
                for (let j = 0; j < setting.value.length; j++) {
                    const arrayItem = setting.value[j];
                    blocks = blocks.concat(arrayItem.getCodeBlocks());
                }
            }
            if (setting.settingType === SettingType.OPTION) {
                const templateSettings = feature.getSettingValue('SETTINGS');
                const mappedSetting = templateSettings.find((s: any) => s.getSettingValue('SETTING_ID') === setting.id);
                if (mappedSetting) {
                    const referencedFeature = project.getFeature(setting.value);
                    if (referencedFeature) {
                        context.feature.settings[setting.id] = referencedFeature.getName();
                    }
                }
            }
        }
        let sourceParams: any = {};
        if (this.id.indexOf('node_') === 0) {
            const links = project.getNodeLinks(this, 'exec');
            if (links.length > 0) {
                const source = project.getFeature(links[0].getSettingValue('SOURCE_FEATURE')) as BaseNode;
                sourceParams = source.getPort(links[0].getSettingValue('SOURCE_PORT'))?.params;
            }
        }
        for (let i = 0; i < CODE_BLOCKS.length; i++) {
            const block = CODE_BLOCKS[i];
            const TYPE = block.getSettingValue('TYPE');
            if (TYPE === 'code') {
                const finalCode = this.parseExpression(block.getSettingValue('CODE'), context);
                const location = block.getSettingValue('LOCATION');
                if (location === 'block_trigger') {
                    triggerCode = finalCode;
                } else {
                    const locationCode = this.parseExpression(location, context);
                    blocks.push({
                        location: locationCode,
                        id: block.getSettingValue('CODE_ID') ? block.getSettingValue('CODE_ID') : `${this.getID()}_${block.getID()}`,
                        template: finalCode,
                    });
                }
            } else if (TYPE === 'file') {
                const fileName = block.getSettingValue('NAME');
                const parts = fileName.split('.');
                const extension = parts[parts.length - 1];
                blocks.push({
                    location: fileName,
                    id: block.getID(),
                    template: block.getSettingValue('CODE'),
                });
            }
        }
        if (FEATURE_TYPE === 'block') {
            for (let i = 0; i < VARIABLES.length; i++) {
                const variable = VARIABLES[i];
                const name = variable.getSettingValue('NAME');
                const dataType = variable.getSettingValue('DATA_TYPE');
                switch (project.runtime) {
                    case 'micropython':
                        triggerCode = `${name}\n${triggerCode}`;
                        break;
                    case 'zephyr':
                        {
                            let DATATYPE = dataType;
                            switch (dataType) {
                                case 'string':
                                    DATATYPE = 'char*';
                                    break;
                                case 'float':
                                    DATATYPE = 'float';
                                    break;
                                case 'integer':
                                    DATATYPE = 'int';
                                    break;
                                case 'boolean':
                                    DATATYPE = 'bool';
                                    break;
                                default:
                                    DATATYPE = 'int';
                                    break;
                            }
                            triggerCode = `${DATATYPE} ${name};\n${triggerCode}`;
                        }
                        break;
                    default:
                        triggerCode = `dim ${name} as ${dataType}\n${triggerCode}`;
                        break;
                }
            }
            let expressionIndex = triggerCode.indexOf('{{');
            let sourceParams: any = {};
            if (FEATURE_TYPE === 'block') {
                const links = project.getNodeLinks(this, 'exec');
                if (links.length > 0) {
                    const source = project.getFeature(links[0].getSettingValue('SOURCE_FEATURE')) as BaseNode;
                    sourceParams = source.getPort(links[0].getSettingValue('SOURCE_PORT'))?.params;
                }
            }
            while (expressionIndex !== -1) {
                const endExpressionIndex = triggerCode.indexOf('}}', expressionIndex);
                const expression = triggerCode.substring(expressionIndex + 2, endExpressionIndex);
                let expressionParsed = expression;
                if (expression.indexOf('$') === 0) {
                    // setting expression
                    const settingId = expression.substring(1);
                    const dataType = this.getSetting(settingId)?.dataType;
                    const settingValue = this.getSettingValue(settingId);
                    expressionParsed = project
                        .parseCodeExpression(settingValue, sourceParams, dataType);
                } else {
                    // javascript expression
                    const context = {
                        feature: FeatureUtils.toJson(this),
                        project: project.toJson(),
                    };
                    // eslint-disable-next-line no-await-in-loop
                    expressionParsed = indirectEvalSync(context, expression);
                }
                triggerCode = triggerCode.substring(0, expressionIndex)
                    + expressionParsed
                    + triggerCode.substring(endExpressionIndex + 2);
                expressionIndex = triggerCode.indexOf('{{', endExpressionIndex);
            }
            // if block name does not start with On, then it is a trigger block
            if (NAME.indexOf('On') !== 0) {
                const links = project.getNodeLinks(this, 'exec');
                const doneBlock = doneLinks.length > 0 ? `${project.getCodeLocationStart()}${doneLinks[0].getSourceId()}${project.getCodeLocationEnd()}` : '';
                for (let i = 0; i < links.length; i += 1) {
                    const link = links[i];
                    blocks.push({
                        location: link.getSourceId(),
                        blockType: BlockType.CODE,
                        id: link.getID(),
                        template: `
    if true then 'custom block scope
        ${triggerCode}
        ${doneBlock}
    end if`,
                    });
                }
            }
        }
        return blocks;
    }
}
